import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button } from 'antd';

import ContextBar from '../components/common/ContextBar';
import GroupHeader from '../components/common/GroupHeader';
import MarkdownContent from '../components/common/MarkdownContent';
import RecordBadge, { RecordIdentity } from '../components/common/RecordBadge';
import InstallPackageModal from '../components/discover/InstallPackageModal';
import InstalledBadge from '../components/discover/InstalledBadge';
import InstalledIn from '../components/discover/InstalledIn';
import PackageVersionHistory from '../components/discover/PackageVersionHistory';
import PackageNeeds from '../components/discover/PackageNeeds';
import ReviewUpdateDialog from '../components/collections/ReviewUpdateDialog';
import TagBadge from '../components/tags/TagBadge';
import PublishedBy from '../components/publishers/PublishedBy';
import { GRID, PLUS } from '../components/icons';
import { chartFormGlyph } from '../components/reports/chartFormGlyphs';
import { chartFormWord } from '../utils/chartForms';
import { addOnAddsLine, elementAddOnsWords, exampleCountFor, formKindWords, getPackage, listingContentsLine, lookupTargetWords, packageContentsLine, packSettingsNote, pausedAutomationsNote, pausedRulesNote, preparedFormsNote, presetAddOns, PACKAGE_KIND_LABELS } from '../services/packages';
import { getDataTypeMeta } from '../utils/dictionary';
import { keptFieldLabel } from '../utils/packageElementRules';
import { isTokenValid } from '../utils';
import { installedInWords } from '../utils/packageInstalls';
import { packageKeyOf } from '../utils/packageAddress';
import { DataState, ResourceState, Skeleton, deriveDataState, resourceStateOf } from '../components/loading';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { hasInstallChoices, parseInstallLink } from '../utils/installLink';

const DISCOVER_CRUMB = { label: 'Discover', to: '/discover' };

/** A key Discover has no package for (an unlisted, retired or mistyped one). */
export const PACKAGE_NOT_FOUND_TEXT = 'There is no such package on Discover.';

/**
 * One card of the package: its identity, what it extends, and its elements on
 * request — an element that comes only with an add-on says which (keepr-api
 * KPR-457: a core card's rollup over an add-on's records waits on it).
 */
function PackageCardRow({ card, byLocal, addOnByKey = new Map() }) {
	const [open, setOpen] = useState(false);
	const count = (card.elements || []).length;
	const parent = card.parent
		? (card.parent.ref ? byLocal.get(card.parent.ref)?.name : card.parent.name)
		: null;
	return (
		<div className="package-page__card">
			<RecordIdentity
				badge={<RecordBadge kind="card" record={card} title={card.name} size="sm" />}
				below={[
					count === 1 ? '1 element' : `${count} elements`,
					parent ? `extends ${parent}` : null,
					card.description || null,
				].filter(Boolean).join(' · ')}
				actions={count ? (
					<Button type="link" onClick={() => setOpen(v => !v)} aria-expanded={open}>
						{open ? 'Hide elements' : 'Show elements'}
					</Button>
				) : null}
			/>
			{open ? (
				<ul className="package-page__elements">
					{card.elements.map(el => (
						<li key={el.name}>
							<span className="package-page__element-name">{el.label?.singular || el.name}</span>
							<span className="package-page__element-type">
								{getDataTypeMeta(el.dataType)?.label || el.dataType}
								{el.lookup ? ` → ${lookupTargetWords(el.lookup, byLocal)}` : ''}
								{groupColumnsWords(el)}
								{el.addOn ? ` · with ${elementAddOnsWords(el, addOnByKey)}` : ''}
							</span>
						</li>
					))}
				</ul>
			) : null}
		</div>
	);
}

/**
 * A repeating group's columns, in words (keepr-api KPR-691: a bundle carries
 * a card's group, and the install makes your own copy): " · Name, Amount,
 * Kind" — each column by its label, as the card editor shows it. Empty for
 * any other element.
 */
function groupColumnsWords(el) {
	const columns = Array.isArray(el.columns) ? el.columns : [];
	if (!columns.length) return '';
	return ` · ${columns.map(c => c.label?.singular || c.name).join(', ')}`;
}

/** A card a package names, in words: one of its own cards, or a global one. */
function cardName(ref, byLocal) {
	if (!ref) return '';
	if (ref.ref) return byLocal.get(ref.ref)?.name || 'a card here';
	return ref.name || ref.globalKey || '';
}

/**
 * One tag a collection package brings (keepr-api tags T10): the tag's own
 * badge, and below it where it sits and what it does — the card its rule
 * tags (the rule arrives paused), or, for a restricted tag, that it comes with
 * no access level. A package names its tags by local id, never an id.
 */
function PackageTagRow({ tag, byLocalTag, byLocal }) {
	const record = { name: tag.name, color: tag.color || null, icon: tag.icon || null, restricted: tag.restricted === true, owner: { kind: 'collection' } };
	const parent = tag.parent ? byLocalTag.get(tag.parent)?.name : null;
	const onNew = (tag.applyToNewItemsOf || []).map(ref => cardName(ref, byLocal)).filter(Boolean);
	const below = [
		parent ? `under ${parent}` : null,
		tag.rule ? `Applied by a rule on ${cardName(tag.rule.card, byLocal)}, arrives paused` : null,
		tag.rule && tag.rule.strict ? 'only the rule applies it' : null,
		tag.restricted ? 'restricted, with no access levels' : null,
		onNew.length ? `put on new ${onNew.join(', ')} items` : null,
	].filter(Boolean).join(' · ');
	return (
		<li className="package-page__tag">
			<RecordIdentity
				badge={<TagBadge tag={record} size="sm" automatic={Boolean(tag.rule)} />}
				below={below || null}
			/>
		</li>
	);
}

/**
 * One chart a collection package brings (keepr-api charts A10): its name, the
 * form it is drawn as and the card it counts ("every card" for none). A
 * package names its cards by local id or global key, never an id; never its
 * question or its filter.
 */
function PackageChartRow({ chart, byLocal, addOnName = null }) {
	const Glyph = chartFormGlyph(chart.form);
	const card = chart.card ? cardName(chart.card, byLocal) : 'every card';
	return (
		<li className="package-page__chart">
			<RecordIdentity
				badge={(
					<span className="package-page__chart-name">
						<Glyph aria-hidden="true" className="package-page__chart-glyph" />
						{chart.name}
					</span>
				)}
				below={[chartFormWord(chart.form), card ? `counts ${card}` : null, addOnName ? `with ${addOnName}` : null].filter(Boolean).join(' · ')}
			/>
		</li>
	);
}

/** One dashboard it brings: its name, how many charts it shows, and whether the collection's Overview shows it. */
function PackageDashboardRow({ dashboard, intoExisting = false, addOnName = null }) {
	const n = dashboard.tiles || 0;
	return (
		<li className="package-page__chart">
			<RecordIdentity
				badge={(
					<span className="package-page__chart-name">
						<GRID aria-hidden="true" className="package-page__chart-glyph" />
						{dashboard.name}
					</span>
				)}
				below={[n === 1 ? '1 chart' : `${n} charts`, dashboard.overview ? (intoExisting ? 'shown on Overview where none is chosen' : 'shown on Overview') : null, addOnName ? `with ${addOnName}` : null].filter(Boolean).join(' · ')}
			/>
		</li>
	);
}

/**
 * "Works with" (KPR-387): what the package's cards look up into. A published
 * collection is a link to it (a global collection is read in place); a
 * platform card is named, with nothing to open. The server lists a
 * published collection only while the bundle's lookup into it is live
 * (keepr-api KPR-384, KPR-462).
 */
function WorksWith({ entries }) {
	if (!entries.length) return null;
	return (
		<section className="package-page__section" aria-label="Works with">
			<GroupHeader as="h2" label="Works with" count={entries.length} />
			<ul className="package-page__works-with">
				{entries.map(entry => (
					<li key={entry.type === 'collection' ? `c:${entry.id}` : `g:${entry.globalKey}`} className="package-page__works-with-row">
						{entry.type === 'collection' ? (
							<RecordIdentity
								badge={<RecordBadge kind="collection" record={{ icon: entry.icon, color: entry.color, name: entry.title }} title={entry.title} size="sm" to={`/discover/global/${entry.id}`} />}
								below="Global collection · open it to see what it holds"
							/>
						) : (
							<RecordIdentity
								badge={<RecordBadge kind="card" record={{ icon: entry.icon, color: entry.color, name: entry.name }} title={entry.name} size="sm" />}
								below="A keepr card its records can point at"
							/>
						)}
					</li>
				))}
			</ul>
		</section>
	);
}

/**
 * One add-on of a bundle (keepr-api KPR-383): its name and one-line
 * description, whether the listing (or the package) ticks it, what it needs,
 * and its cards — each with its elements on request, as the core's.
 */
function PackageAddOnRow({ addOn, byLocal, byKey, ticked, listingName, dashboardsByLocal }) {
	const cards = (addOn.cards || []).map(local => byLocal.get(local)).filter(Boolean);
	const needs = (addOn.needs || []).map(key => byKey.get(key)?.name || key);
	// Its own examples (keepr-api KPR-465), every set.
	const examples = addOn.contents?.items || 0;
	const below = [
		ticked ? (listingName ? `In ${listingName}` : 'Ticked when you add it') : 'Not ticked when you add it',
		needs.length ? `needs ${needs.join(', ')}` : null,
		// A no-break space: the facts wrap, the count and its noun do not part.
		examples ? (examples === 1 ? '1\u00a0example' : `${examples}\u00a0examples`) : null,
	].filter(Boolean).join(' · ');
	// What it adds to the core's cards and dashboards (keepr-api KPR-457).
	const adds = addOnAddsLine(addOn, { cardsByLocal: byLocal, dashboardsByLocal, addOnByKey: byKey });
	return (
		<li className="package-page__addon">
			<div className="package-page__addon-head">
				<span className="package-page__addon-name">{addOn.name}</span>
				{addOn.description ? <span className="package-page__addon-description">{addOn.description}</span> : null}
				<span className="meta-line">{below}</span>
				{adds ? <span className="meta-line package-page__addon-adds">{adds}.</span> : null}
			</div>
			{cards.map(card => <PackageCardRow key={card.localId} card={card} byLocal={byLocal} addOnByKey={byKey} />)}
		</li>
	);
}

/**
 * /discover/:key — one package (plan §3.3): what it is, what is inside it,
 * and the one action — **Add to collection…** for a card or a bundle,
 * **Create collection** for a collection package. Another publisher's
 * package opens at /discover/:publisher/:name (keepr-api KPR-358: its key is
 * `<publisher>/<name>`); an unknown one says so as a bare key does.
 */
export default function PackagePage() {
	const packageKey = packageKeyOf(useParams());
	// A bundle's listing preset (keepr-api KPR-383): the tile it was opened
	// from — its name, its description, and the add-ons it ticks.
	const [searchParams] = useSearchParams();
	const listingKey = searchParams.get('listing');
	// A link an assistant gave (KPR-1439): ?install=1 opens Add to collection
	// with the link's choices made; it never adds anything by itself.
	const searchText = searchParams.toString();
	const link = useMemo(() => parseInstallLink(new URLSearchParams(searchText)), [searchText]);
	const prefill = useMemo(() => (hasInstallChoices(link) ? link : null), [link]);
	const { user } = useCurrentUser();
	const linkOpened = useRef(false);
	const [pkg, setPkg] = useState(null);
	const [error, setError] = useState(null);
	const [installing, setInstalling] = useState(false);
	// The collection an *Add an add-on* on a row of *In your collections* was
	// pressed for (KPR-364); null for the page's own button.
	const [addOnFor, setAddOnFor] = useState(null);
	// The row of *In your collections* whose update is being reviewed (KPR-458).
	const [updateFor, setUpdateFor] = useState(null);
	// Version history, opened from the version in the header (KPR-1882).
	const [versionsOpen, setVersionsOpen] = useState(false);
	// Retry bumps this so the package is read again.
	const [reloadKey, setReloadKey] = useState(0);

	useEffect(() => {
		let live = true;
		setPkg(null);
		setError(null);
		getPackage(packageKey)
			.then(data => { if (live) setPkg(data); })
			.catch(e => { if (live) setError(e); });
		return () => { live = false; };
	}, [packageKey, reloadKey]);

	// Once per visit, for a signed-in person whose address is confirmed (an
	// unconfirmed one would only be refused); the page stays as it is when not.
	useEffect(() => {
		if (!link.install || linkOpened.current || !pkg || !user || user.emailVerified === false || !isTokenValid()) return;
		linkOpened.current = true;
		setAddOnFor(null);
		setInstalling(true);
	}, [link, pkg, user]);

	const notFound = !pkg && resourceStateOf(error) === 'not-found';
	const isCollection = pkg?.kind === 'collection';
	const preview = Array.isArray(pkg?.preview) ? pkg.preview : [];
	const byLocal = new Map((pkg?.cards || []).map(card => [card.localId, card]));
	const tags = Array.isArray(pkg?.tags) ? pkg.tags : [];
	const byLocalTag = new Map(tags.map(tag => [tag.localId, tag]));
	const ruled = tags.filter(tag => tag.rule).length;
	const charts = Array.isArray(pkg?.charts) ? pkg.charts : [];
	const dashboards = Array.isArray(pkg?.dashboards) ? pkg.dashboards : [];
	const dashboardsByLocal = new Map(dashboards.map(d => [d.localId, d]));
	// Its automations and notifications (keepr-api KPR-419 / KPR-361): each
	// arriving paused, with a sentence of what it does (KPR-1433; null when
	// the rule cannot be read, and then only its name shows).
	const automations = Array.isArray(pkg?.automations) ? pkg.automations : [];
	const notifications = Array.isArray(pkg?.notifications) ? pkg.notifications : [];
	// Its forms (keepr-api KPR-360): name, kind and card only — each arrives
	// prepared and off.
	const forms = Array.isArray(pkg?.forms) ? pkg.forms : [];
	// Who sees which element (keepr-api KPR-1710): each element it keeps from
	// some people, with a sentence — switched on as soon as it is added.
	const keptElements = Array.isArray(pkg?.elementRules) ? pkg.elementRules : [];
	const cta = isCollection ? 'Create collection' : 'Add to collection…';
	// A bundle's add-ons (keepr-api KPR-383): the core is every card no add-on
	// brings; the listing (or the package's defaults) says which are ticked.
	const addOns = Array.isArray(pkg?.addOns) ? pkg.addOns : [];
	const addOnByKey = new Map(addOns.map(a => [a.key, a]));
	const listing = (pkg?.listings || []).find(l => l.key === listingKey) || null;
	const ticked = new Set(pkg ? presetAddOns(pkg, listing?.key) : []);
	// Its examples (keepr-api KPR-465): a bundle's for what the listing (or the
	// package) ticks, in the listing's sets; any other package, all its items.
	const items = pkg ? (addOns.length ? exampleCountFor(pkg, [...ticked], listing?.key) : (pkg.contents?.items || 0)) : 0;
	const coreCards = (pkg?.cards || []).filter(card => !addOns.length || !card.addOn);
	const title = listing ? listing.name : pkg?.name;
	const description = listing ? (listing.description || pkg?.description) : pkg?.description;
	// What it is good for and the words it is found by (keepr-api KPR-1431): the
	// opened listing's own, else the package's. Plain text, never tag badges.
	const goodFor = ((listing ? listing.goodFor : pkg?.goodFor) || []).filter(Boolean);
	const keywords = ((listing ? listing.keywords : pkg?.keywords) || []).filter(Boolean);
	// A card or bundle package adds its own cards' views to the collection it
	// goes into, never over the person's own (keepr-api KPR-338).
	const settingsNote = pkg && !isCollection ? packSettingsNote(pkg.contents) : '';

	return (
		<>
			<ContextBar
				breadcrumb={[DISCOVER_CRUMB]}
				kind={isCollection ? 'collection' : 'card'}
				{...(pkg && pkg.kind === 'bundle' && preview.length > 1 ? { records: preview } : { record: pkg ? { icon: pkg.icon, color: pkg.color, name: pkg.name } : null })}
				title={title || 'Package'}
				below={pkg ? (
					<>
						{[PACKAGE_KIND_LABELS[pkg.kind], pkg.category, listing ? `part of ${pkg.name}` : null, listing ? listingContentsLine(pkg, listing) : packageContentsLine(pkg)].filter(Boolean).join(' · ')}
						{/* The version listed now; pressed, every version Discover has listed (KPR-1882). */}
						{pkg.version ? <> · <Button type="link" className="package-page__version" onClick={() => setVersionsOpen(true)}>Version {pkg.version}</Button></> : null}
						{/* Who publishes it (KPR-385), with its logo where there is room. */}
						{pkg.publisher ? <> · <PublishedBy publisher={pkg.publisher} logo link /></> : null}
						{/* Whether the person has it (KPR-364): where, and — for a manager of a collection that is behind — that an update is out. */}
						{installedInWords(pkg) ? <> · <InstalledBadge pkg={pkg} className="installed-badge--inline" /> {installedInWords(pkg)}</> : null}
					</>
				) : null}
				actions={pkg && isTokenValid() ? (
					<Button type="primary" icon={<PLUS />} onClick={() => { setAddOnFor(null); setInstalling(true); }}>{cta}</Button>
				) : null}
			/>

			{notFound ? (
				<ResourceState state="not-found" body={PACKAGE_NOT_FOUND_TEXT} />
			) : (
			<DataState
				state={deriveDataState({ loading: !pkg && !error, error: pkg ? null : error })}
				hasData={Boolean(pkg)}
				skeleton={<Skeleton variant="list-row" count={6} />}
				error={error ? 'Check your connection and try again.' : null}
				onRetry={() => setReloadKey(n => n + 1)}
				label="this package"
			>
			{pkg ? (
				<div className="package-page">
					{description ? <MarkdownContent markdown={description} className="package-page__description" /> : null}

					{goodFor.length ? (
						<section className="package-page__good-for" aria-label="Good for">
							<h2 className="micro-label">Good for</h2>
							<ul>
								{goodFor.map(line => <li key={line}>{line}</li>)}
							</ul>
						</section>
					) : null}
					{keywords.length ? (
						<ul className="package-page__keywords" aria-label="Keywords">
							{keywords.map(word => <li key={word} className="package-page__keyword">{word}</li>)}
						</ul>
					) : null}

					<InstalledIn pkg={pkg} onAddAddOn={collectionId => { setAddOnFor(collectionId); setInstalling(true); }} onReviewUpdate={entry => setUpdateFor({ collectionId: entry.collectionId, key: pkg.key, name: pkg.name })} />

					{items ? (
						<Alert
							type="info"
							showIcon
							className="package-page__items"
							message={pkg.contents?.itemsRole === 'demo'
								? `Comes with ${items === 1 ? 'an example item' : `${items} example items`} if you ask for them when you add it. You can remove them in one step.`
								: `Comes with ${items === 1 ? 'one record' : `${items} records`} the cards are built around.`}
						/>
					) : null}

					<PackageNeeds pkg={pkg} addOnByKey={addOnByKey} />

					<section className="package-page__section" aria-label="What's inside">
						<GroupHeader as="h2" label={addOns.length ? 'The core' : "What's inside"} count={coreCards.length} countLabel="card" />
						{addOns.length ? <p className="meta-line package-page__core-note">Every collection it is added to gets these.</p> : null}
						{coreCards.map(card => <PackageCardRow key={card.localId} card={card} byLocal={byLocal} addOnByKey={addOnByKey} />)}
						{(pkg.elementSets || []).length ? (
							<p className="meta-line package-page__sets">
								Also brings {pkg.elementSets.length === 1 ? 'an element set' : `${pkg.elementSets.length} element sets`}: {pkg.elementSets.map(s => s.name).join(', ')}.
							</p>
						) : null}
						{settingsNote ? <p className="meta-line package-page__settings">{settingsNote}</p> : null}
					</section>

					<WorksWith entries={Array.isArray(pkg.worksWith) ? pkg.worksWith : []} />

					{addOns.length ? (
						<section className="package-page__section" aria-label="Add-ons">
							<GroupHeader as="h2" label="Add-ons" count={addOns.length} countLabel="add-on" />
							<p className="meta-line package-page__addons-note">Choose them when you add it, or add one later to a collection that has it.</p>
							<ul className="package-page__addons">
								{addOns.map(addOn => (
									<PackageAddOnRow key={addOn.key} addOn={addOn} byLocal={byLocal} byKey={addOnByKey} ticked={ticked.has(addOn.key)} listingName={listing?.name} dashboardsByLocal={dashboardsByLocal} />
								))}
							</ul>
						</section>
					) : null}

					{tags.length ? (
						<section className="package-page__section" aria-label="Tags">
							<GroupHeader as="h2" label="Tags" count={tags.length} countLabel="tag" />
							<ul className="package-page__tags">
								{tags.map(tag => <PackageTagRow key={tag.localId} tag={tag} byLocalTag={byLocalTag} byLocal={byLocal} />)}
							</ul>
							{ruled ? <p className="meta-line package-page__paused">{pausedRulesNote(ruled)}</p> : null}
						</section>
					) : null}

					{charts.length || dashboards.length ? (
						<section className="package-page__section" aria-label="Charts and dashboards">
							<GroupHeader as="h2" label="Charts and dashboards" count={charts.length + dashboards.length} />
							<ul className="package-page__charts">
								{dashboards.map(dashboard => <PackageDashboardRow key={dashboard.localId} dashboard={dashboard} intoExisting={!isCollection} addOnName={dashboard.addOn ? elementAddOnsWords(dashboard, addOnByKey) : null} />)}
								{charts.map(chart => <PackageChartRow key={chart.localId} chart={chart} byLocal={byLocal} addOnName={chart.addOn ? elementAddOnsWords(chart, addOnByKey) : null} />)}
							</ul>
							<p className="meta-line package-page__paused">{isCollection
								? 'Each is for everyone in the new collection, counting the items each person can see.'
								: 'Each is for everyone in the collection you add it to, counting the items each person can see.'}</p>
						</section>
					) : null}

					{automations.length || notifications.length ? (
						<section className="package-page__section" aria-label="Automations and notifications">
							<GroupHeader as="h2" label="Automations and notifications" count={automations.length + notifications.length} />
							<ul className="package-page__rules">
								{automations.map((rule, index) => (
									<li key={`a${index}`} className="package-page__rule">
										<RecordIdentity badge={<span className="package-page__rule-name">{rule.name}</span>} below="Automation · arrives paused" />
										{rule.summary ? <p className="meta-line package-page__rule-summary">{rule.summary}</p> : null}
									</li>
								))}
								{notifications.map((def, index) => (
									<li key={`n${index}`} className="package-page__rule">
										<RecordIdentity badge={<span className="package-page__rule-name">{def.name}</span>} below="Notification · arrives paused" />
										{def.summary ? <p className="meta-line package-page__rule-summary">{def.summary}</p> : null}
									</li>
								))}
							</ul>
							<p className="meta-line package-page__paused">{pausedAutomationsNote({ automations: automations.length, notifications: notifications.length })}</p>
						</section>
					) : null}

					{forms.length ? (
						<section className="package-page__section" aria-label="Forms" data-testid="package-page-forms">
							<GroupHeader as="h2" label="Forms" count={forms.length} />
							<ul className="package-page__rules">
								{forms.map((form, index) => {
									const card = form.card?.ref ? byLocal.get(form.card.ref) : null;
									const kind = formKindWords(form.kind);
									return (
										<li key={`f${index}`} className="package-page__rule">
											<RecordIdentity
												badge={<span className="package-page__rule-name">{form.name}</span>}
												below={`${kind.charAt(0).toUpperCase()}${kind.slice(1)}${card ? ` for ${card.name}` : ''} · arrives prepared and off`}
											/>
										</li>
									);
								})}
							</ul>
							<p className="meta-line package-page__paused">{preparedFormsNote(forms.length)}</p>
						</section>
					) : null}

					{keptElements.length ? (
						<section className="package-page__section" aria-label="Who sees which elements" data-testid="package-page-element-rules">
							<GroupHeader as="h2" label="Who sees which elements" count={keptElements.length} />
							<ul className="package-page__rules">
								{keptElements.map((rule, index) => {
									const card = rule.card?.ref ? byLocal.get(rule.card.ref) : null;
									const cardName = card?.name || rule.card?.name || '';
									const addOn = rule.addOn ? addOnByKey.get(rule.addOn) : null;
									return (
										<li key={`r${index}`} className="package-page__rule">
											<RecordIdentity
												badge={<span className="package-page__rule-name">{keptFieldLabel(rule)}</span>}
												below={`${cardName ? `On ${cardName}` : 'An element'}${addOn ? ` · comes with ${addOn.name}` : ''}`}
											/>
											{rule.summary ? <p className="meta-line package-page__rule-summary">{rule.summary}</p> : null}
										</li>
									);
								})}
							</ul>
							<p className="meta-line package-page__paused">Each rule is switched on as soon as it is added. A rule your collection already has for an element is kept, and the package&rsquo;s is not applied.</p>
						</section>
					) : null}

					<p className="meta-line package-page__note">
						What you add is a copy you own: change it as you like. Nothing you write is shared with anyone.
					</p>
				</div>
			) : null}
			</DataState>
			)}

			<PackageVersionHistory pkg={pkg} open={versionsOpen} onClose={() => setVersionsOpen(false)} />
			<InstallPackageModal pkg={pkg} listingKey={listing?.key || null} presetTarget={addOnFor} prefill={prefill} open={installing} onClose={() => setInstalling(false)} />
			{updateFor ? (
				<ReviewUpdateDialog
					bundleKey={updateFor.key}
					bundleName={updateFor.name}
					collectionId={updateFor.collectionId}
					onClose={() => setUpdateFor(null)}
					// Read the rows again in place: the version and Update available move.
					onChanged={() => { getPackage(packageKey).then(setPkg).catch(() => {}); }}
				/>
			) : null}
		</>
	);
}
